import { Button, Flex, Grid, Group, Paper, Text, rem } from "@mantine/core"
import { ArrowLeftIcon, ArrowsClockwiseIcon, FileIcon } from "@phosphor-icons/react"
import { ReactNode, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { DisabledReason, HelpButtonGroup, HelpIcon, StatusBadgeHelp, useHelpContext, useStatusHelp } from "@dehub/theme"
import { dateTimeSecFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { PageLayout } from "../layout"
import { StatusBar } from "../odoo/status-bar"
import { CodeBadge } from "../ui/code-badge"
import { DotPill, Tone } from "../delivery/atoms"
import { SupplierShippingCustomerDetailDeliveryNoteList } from "./delivery-note.list"
import { PULL_SHEET_COLOR, PULL_SHEET_FLOW, usePullSheetStatus } from "./pull-sheet-status"
import { usePullSheetPdf } from "./use-pull-sheet-pdf"

/** Status as a dot pill (Ord_ShipDetail); same hues as PULL_SHEET_COLOR. */
const PS_TONE: Record<string, Tone> = { APPROVED: "grey", LOADING: "amber", CONFIRMED: "blue", LOADED: "green" }

function Field({ label, mono, children }: Readonly<{ label: string; mono?: boolean; children: ReactNode }>) {
  return (
    <Grid.Col span={{ xs: 12, sm: 6 }}>
      <Text fz={14} c="gray.8" mb={2}>
        {label}
      </Text>
      <Text fz={14} fw={500} c="dark" ff={mono ? "monospace" : undefined}>
        {children}
      </Text>
    </Grid.Col>
  )
}

function UserField({ label, user }: Readonly<{ label: string; user: any }>) {
  if (!user) return <Field label={label}>-</Field>
  return (
    <Grid.Col span={12}>
      <Text fz={14} c="gray.8" mb={2}>
        {label}
      </Text>
      <Group gap={6}>
        <Text fz={14} fw={700} c="dark">
          {renderName(user)}
        </Text>
        {user?.phone && (
          <Text fz={14} c="gray.8">
            ({user.phone})
          </Text>
        )}
      </Group>
      <Text fz={14} c="gray.8">
        {user?.email || "-"}
      </Text>
    </Grid.Col>
  )
}

// Canvas card: slate hairline, radius 14 (base.css --nw-table-*).
const CARD_STYLES = { root: { border: "1px solid var(--nw-table-border)", borderRadius: rem(14), boxShadow: "var(--nw-table-shadow)" } }

export function SupplierShippingCustomerDetailForm({ action, reload }: Readonly<{ action: any[]; reload?: () => void }>) {
  const navigate = useNavigate()
  const { t } = useTranslation(["delivery", "common"])
  const { shownStateOf } = useDeliveryActions()
  const { nameOf } = usePullSheetStatus()
  const pdf = usePullSheetPdf()
  const d = action[1]

  // One status list, one set of names (delivery:psStatus.*) for the stepper and the badge (O217).
  const pipeline = PULL_SHEET_FLOW.map((code) => ({ code, name: nameOf(code), color: PULL_SHEET_COLOR[code] }))
  const code: string = d?.pullSheetStatus || "APPROVED"
  const pdfState = shownStateOf("pullSheetPdf", d)
  const regenerateState = shownStateOf("pullSheetPdfRegenerate", d)

  // @dehub/help: the pull sheet status drives the drawer's «Одоо» step and the status tab; the stepper gets step popovers.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({ status: code })
  }, [code, setHelpContext])
  const psStepHelp = useStatusHelp("order.ps-status")
  const pdfReason = (st: { allowed: boolean; reason: string | null }) => (st.allowed ? null : /эрх|permission/i.test(st.reason ?? "") ? "PERM" : "NOT_READY")

  const refreshAfterRegenerate = async () => {
    await pdf.open(d.id, { regenerate: true })
    reload?.()
  }

  return (
    <PageLayout
      title={t("delivery:pullSheet.detailTitle")}
      subTitle={t("delivery:pullSheet.detailSubTitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home", { ns: "common" }) },
        { to: "/supplier-shipping", label: t("delivery:breadcrumb.loadingManagement") },
        { label: t("delivery:pullSheet.pullSheet") },
      ]}
      extra={[
        // @dehub/help: attached «?» (R2) + reason popover while unavailable (R4).
        <DisabledReason key="pdf" helpKey="order.supplier-shipping-detail.reason.pdf" reason={pdfReason(pdfState)} when={!pdfState.allowed} position="bottom-end">
          <HelpButtonGroup helpKey="order.supplier-shipping-detail.action.view-pdf">
            <DeliveryActionButton data-help-id="pull-sheet-pdf" state={pdfState} variant="default" leftSection={<FileIcon size={16} />} loading={pdf.loadingId === d?.id} onClick={() => pdf.open(d.id, { cachedUrl: d.pdf })}>
              {t("delivery:pullSheet.viewPdf")}
            </DeliveryActionButton>
          </HelpButtonGroup>
        </DisabledReason>,
        <DisabledReason key="regen" helpKey="order.supplier-shipping-detail.reason.pdf" reason={pdfReason(regenerateState)} when={!regenerateState.allowed} position="bottom-end">
          <HelpButtonGroup helpKey="order.supplier-shipping-detail.action.regenerate-pdf">
            <DeliveryActionButton state={regenerateState} variant="default" leftSection={<ArrowsClockwiseIcon size={16} />} loading={pdf.loadingId === d?.id} onClick={refreshAfterRegenerate}>
              {t("delivery:psDetail.pdfRegenerate")}
            </DeliveryActionButton>
          </HelpButtonGroup>
        </DisabledReason>,
        <Button key="back" variant="default" leftSection={<ArrowLeftIcon size={16} />} onClick={() => navigate("/supplier-shipping")}>
          {t("common:actions.back", { ns: "common" })}
        </Button>,
      ]}
    >
      <Flex direction="column" gap="md">
        <StatusBar statuses={pipeline} currentStatus={code} getStepHelp={psStepHelp} />

        <Grid>
          <Grid.Col span={{ xs: 12, md: 6 }}>
            <Paper p="md" bg="white" h="100%" styles={CARD_STYLES}>
              <Text fz="var(--nw-fz-card)" fw={700} mb="sm">
                {t("delivery:pullSheet.loadingInfo")}
                <HelpIcon helpKey="order.supplier-shipping-detail.section.loading-info" variant="section" />
              </Text>
              <Grid gap="sm">
                <Field label={t("delivery:pullSheet.code")} mono>{d?.refCode || "-"}</Field>
                <Grid.Col span={{ xs: 12, sm: 6 }}>
                  <Text fz={14} c="gray.8" mb={2}>
                    {t("delivery:pullSheet.route")}
                  </Text>
                  {d?.route ? (
                    <Group gap={6} wrap="wrap">
                      <Text fz={14} fw={700} ff="monospace">
                        {d.route.routeNumber}
                      </Text>
                      {d.route.name && (
                        <Text fz={14} fw={500} c="dark">
                          {d.route.name}
                        </Text>
                      )}
                    </Group>
                  ) : (
                    <Text fz={14} fw={500}>
                      -
                    </Text>
                  )}
                </Grid.Col>
                <Grid.Col span={{ xs: 12, sm: 6 }}>
                  <Text fz={14} c="gray.8" mb={2}>
                    {t("delivery:pullSheet.status")}
                  </Text>
                  <StatusBadgeHelp helpNs="order.ps-status" code={code} name={nameOf(d?.pullSheetStatus)}>
                    <DotPill tone={PS_TONE[d?.pullSheetStatus as string] ?? "grey"}>{nameOf(d?.pullSheetStatus)}</DotPill>
                  </StatusBadgeHelp>
                </Grid.Col>
                <Field label={t("delivery:pullSheet.loadingStarted")} mono>{dateTimeSecFormat(d?.startedDate)}</Field>
                <Field label={t("delivery:pullSheet.loadingEnded")} mono>{dateTimeSecFormat(d?.endedDate)}</Field>
                <Field label={t("delivery:pullSheet.warehouseName")}>{d?.warehouse?.name || "-"}</Field>
                <Field label={t("delivery:psDetail.createdDate")} mono>{dateTimeSecFormat(d?.createdAt)}</Field>
                <Field label={t("delivery:psDetail.dispatchedDate")} mono>{dateTimeSecFormat(d?.dispatchedDate)}</Field>
                <Grid.Col span={{ xs: 12, sm: 6 }}>
                  <Text fz={14} c="gray.8" mb={2}>
                    {t("delivery:pullSheet.orderCount")} ({d?.deliveryNotes?.length ?? 0})
                  </Text>
                  <Flex gap={4} wrap="wrap">
                    {d?.deliveryNotes?.length ? (
                      d.deliveryNotes.map((c: any) => <CodeBadge key={c?.id ?? c?.refCode} variant="light">{c?.refCode}</CodeBadge>)
                    ) : (
                      <Text fz={14} fw={500}>
                        -
                      </Text>
                    )}
                  </Flex>
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ xs: 12, md: 6 }}>
            <Paper p="md" bg="white" h="100%" styles={CARD_STYLES}>
              <Text fz="var(--nw-fz-card)" fw={700} mb="sm">
                {t("delivery:pullSheet.responsibleStaff")}
                <HelpIcon helpKey="order.supplier-shipping-detail.section.responsible" variant="section" />
              </Text>
              <Grid gap="sm">
                <UserField label={t("delivery:pullSheet.deliveryStaff")} user={d?.staff} />
                <UserField label={t("delivery:pullSheet.warehouseStaff")} user={d?.warehouseStaff} />
                <UserField label={t("delivery:pullSheet.loadingApprover")} user={d?.regUser} />
              </Grid>
            </Paper>
          </Grid.Col>
        </Grid>

        <Paper bg="white" styles={{ root: { ...CARD_STYLES.root, overflow: "hidden" } }}>
          <Text fz="var(--nw-fz-card)" fw={700} px={20} py="md">
            {t("delivery:psDetail.linesTitle")}
            <HelpIcon helpKey="order.supplier-shipping-detail.section.lines" variant="section" />
          </Text>
          <SupplierShippingCustomerDetailDeliveryNoteList list={d?.pullSheetLines || []} deliveryNotes={d?.deliveryNotes || []} />
        </Paper>
      </Flex>
    </PageLayout>
  )
}
